import { startAuthentication } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  useHardwareWalletSigning,
  type HardwareWalletSigningState,
} from '@/hooks/useHardwareWalletSigning';
import { trpcClient } from '@/utils/trpc';
import type {
  SigningRequestModification,
  SponsorshipMode,
} from '@infinex-private/connect-types';
import type { SigningRequestMetadata } from '@infinex/connect-service-contract/client';
import type { ExternalSigningDeviceType } from '@infinex/types';

import { usePopup } from './PopupProvider';
import { denyMessageFor } from './signingDenialMessages';

type AuthorizeSigningRequestContextValue = {
  isLoadingOptions: boolean;
  isAuthorizing: boolean;
  metadata: SigningRequestMetadata | undefined;
  requestId: string;
  authorize: () => Promise<void>;
  errorMessage: string | null;
  selectedSponsorshipMode: SponsorshipMode;
  setSelectedSponsorshipMode: (mode: SponsorshipMode) => void;
  /** True if this signing request requires hardware wallet signing */
  isHardwareWallet: boolean;
  /** The type of hardware wallet device (ledger or trezor) */
  hardwareWalletDeviceType: ExternalSigningDeviceType | null;
  hardwareWalletState: HardwareWalletSigningState;
  hardwareWalletError: Error | null;
  /** Resets the hardware wallet state and retries the signing */
  retryHardwareWallet: () => void;
  /** Pending modifications to apply to the signing request */
  modifications: SigningRequestModification[];
  /** Add or update a modification (keyed by type + token + spender) */
  setModification: (modification: SigningRequestModification) => void;
};

const AuthorizeSigningRequestContext =
  createContext<AuthorizeSigningRequestContextValue | null>(null);

export function AuthorizeSigningRequestProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { popupData, sendRequestProcessedNotification, dappId } = usePopup();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedSponsorshipMode, setSelectedSponsorshipMode] =
    useState<SponsorshipMode>('sponsored');
  const [modifications, setModifications] = useState<
    SigningRequestModification[]
  >([]);
  const requestId = popupData?.txnRequestId ?? '';

  const {
    state: hardwareWalletState,
    error: hardwareWalletError,
    signWithHardwareWallet,
    reset: resetHardwareWallet,
  } = useHardwareWalletSigning();

  const { isPending: isLoadingOptions, data: metadata } = useQuery({
    queryKey: ['signing-request-metadata', requestId],
    queryFn: () =>
      trpcClient.app.signingRequest.getSigningRequestMetadata.query({
        requestId,
      }),
    enabled: !!requestId && !!dappId,
  });

  // Determine if this is an externally signed request (hardware wallet)
  const isExternalSigning = useMemo(() => {
    if (!metadata) return false;
    const inputType = metadata.input?.type;

    // evm:transaction with isExternallySigned flag requires external signing
    if (inputType === 'evm:transaction' && metadata.input?.isExternallySigned)
      return true;

    // evm:message and evm:typedDataV4 with isExternallySigned flag
    if (
      (inputType === 'evm:message' || inputType === 'evm:typedDataV4') &&
      metadata.input?.isExternallySigned
    ) {
      return true;
    }

    // SVM types with isExternallySigned flag
    if (
      (inputType === 'svm:transaction' ||
        inputType === 'svm:signMessage' ||
        inputType === 'svm:batchTransactions') &&
      metadata.input?.isExternallySigned
    ) {
      return true;
    }

    return false;
  }, [metadata]);

  // Get the device type from metadata
  const hardwareWalletDeviceType =
    useMemo((): ExternalSigningDeviceType | null => {
      if (!isExternalSigning || !metadata?.externalSigningDevice) return null;
      return metadata.externalSigningDevice.deviceType ?? null;
    }, [isExternalSigning, metadata]);

  // Hardware wallets ALWAYS pay gas with native ETH
  useEffect(() => {
    if (isExternalSigning) {
      setSelectedSponsorshipMode('native');
    }
  }, [isExternalSigning]);

  const getAuthorizeWebAuthnOptionsMutation = useMutation({
    mutationFn: (params: { requestId: string }) =>
      trpcClient.app.signingRequest.getAuthorizeWebAuthnOptions.mutate(params),
  });

  const authorizeSigningRequest = useMutation({
    mutationFn: (params: {
      requestId: string;
      authCodeChallenge?: string | null;
      attestation: AuthenticationResponseJSON | undefined;
      sponsorshipMode?: SponsorshipMode;
      externalSignature?: string;
      modifications?: SigningRequestModification[];
    }) => trpcClient.app.signingRequest.authorize.mutate(params),
  });

  const setModification = useCallback(
    (modification: SigningRequestModification) => {
      setModifications((prev) => {
        if (modification.type === 'evm:approval') {
          const filtered = prev.filter(
            (m) =>
              !(
                m.type === 'evm:approval' &&
                m.token.toLowerCase() === modification.token.toLowerCase() &&
                m.spender.toLowerCase() === modification.spender.toLowerCase()
              )
          );
          return [...filtered, modification];
        }
        return [...prev, modification];
      });
    },
    []
  );

  /**
   * Gets the external signature from a hardware wallet based on the request type.
   */
  const getHardwareWalletSignature = useCallback(async (): Promise<string> => {
    if (!metadata) {
      throw new Error('Missing request metadata');
    }
    // Cast needed due to serialization differences in the query response
    return signWithHardwareWallet(metadata as SigningRequestMetadata);
  }, [metadata, signWithHardwareWallet]);

  const authorize = useCallback(async () => {
    setErrorMessage(null);

    try {
      // For hardware wallets: sign first, then authorize with the signature
      if (isExternalSigning) {
        const externalSignature = await getHardwareWalletSignature();
        await authorizeSigningRequest.mutateAsync({
          requestId,
          authCodeChallenge: null,
          attestation: undefined,
          sponsorshipMode: selectedSponsorshipMode,
          externalSignature,
          modifications: modifications.length > 0 ? modifications : undefined,
        });
        sendRequestProcessedNotification(null);
        return;
      }

      // For passkey: get webauthn options, authenticate, then authorize
      let attestation: AuthenticationResponseJSON;
      try {
        const { webauthnOptions } =
          await getAuthorizeWebAuthnOptionsMutation.mutateAsync({ requestId });
        attestation = await startAuthentication({
          optionsJSON: webauthnOptions,
        });
      } catch {
        setErrorMessage('Authentication was cancelled. Please try again.');
        return;
      }

      await authorizeSigningRequest.mutateAsync({
        requestId,
        authCodeChallenge: null,
        attestation,
        sponsorshipMode: selectedSponsorshipMode,
        modifications: modifications.length > 0 ? modifications : undefined,
      });
      sendRequestProcessedNotification(null);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Authorization failed');
      setErrorMessage(denyMessageFor(err) ?? error.message);
    }
  }, [
    requestId,
    isExternalSigning,
    selectedSponsorshipMode,
    modifications,
    getHardwareWalletSignature,
    getAuthorizeWebAuthnOptionsMutation,
    authorizeSigningRequest,
    sendRequestProcessedNotification,
  ]);

  const isAuthorizing =
    authorizeSigningRequest.isPending ||
    (isExternalSigning &&
      hardwareWalletState !== 'idle' &&
      hardwareWalletState !== 'success' &&
      hardwareWalletState !== 'error');

  const retryHardwareWallet = useCallback(() => {
    resetHardwareWallet();
    setErrorMessage(null);
    void authorize();
  }, [resetHardwareWallet, authorize]);

  const value = useMemo(
    () => ({
      isLoadingOptions,
      isAuthorizing,
      // Cast needed due to serialization differences in the query response
      metadata: metadata as SigningRequestMetadata | undefined,
      requestId,
      authorize,
      errorMessage,
      selectedSponsorshipMode,
      setSelectedSponsorshipMode,
      isHardwareWallet: isExternalSigning,
      hardwareWalletDeviceType,
      hardwareWalletState,
      hardwareWalletError,
      retryHardwareWallet,
      modifications,
      setModification,
    }),
    [
      isLoadingOptions,
      isAuthorizing,
      metadata,
      requestId,
      authorize,
      errorMessage,
      selectedSponsorshipMode,
      isExternalSigning,
      hardwareWalletDeviceType,
      hardwareWalletState,
      hardwareWalletError,
      retryHardwareWallet,
      modifications,
      setModification,
    ]
  );

  return (
    <AuthorizeSigningRequestContext.Provider value={value}>
      {children}
    </AuthorizeSigningRequestContext.Provider>
  );
}

export function useAuthorizeSigningRequest(): AuthorizeSigningRequestContextValue {
  const context = useContext(AuthorizeSigningRequestContext);
  if (!context) {
    throw new Error(
      'useAuthorizeSigningRequest must be used within an AuthorizeSigningRequestProvider'
    );
  }
  return context;
}
